import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import type { UserRole } from "@/contexts/AuthContext";
import { LOGO_FULL, LOGO_ICON } from "@/lib/branding";
import { useBranding } from "@/contexts/BrandingContext";

interface NavItem {
  to: string;
  icon: string;
  label: string;
  roles: UserRole[];
}

const navItems: NavItem[] = [
  { to: "/dashboard", icon: "ri-dashboard-3-line", label: "Dashboard", roles: ["super_admin", "admin"] },
  { to: "/cursos", icon: "ri-book-open-line", label: "Cursos", roles: ["super_admin", "admin"] },
  { to: "/diplomados", icon: "ri-graduation-cap-line", label: "Diplomados", roles: ["super_admin", "admin"] },
  { to: "/estudiantes", icon: "ri-user-star-line", label: "Estudiantes", roles: ["super_admin", "admin"] },
  { to: "/pagos", icon: "ri-money-dollar-circle-line", label: "Recibos de Pago", roles: ["super_admin", "admin"] },
  { to: "/portal-instructor", icon: "ri-presentation-line", label: "Portal Instructor", roles: ["super_admin", "admin", "instructor"] },
  { to: "/portal-evaluador", icon: "ri-check-double-line", label: "Portal Evaluador", roles: ["super_admin", "admin", "evaluador"] },
  { to: "/certificados", icon: "ri-award-line", label: "Certificados", roles: ["super_admin", "admin"] },
  { to: "/contactos", icon: "ri-mail-line", label: "Contactos", roles: ["super_admin", "admin", "marketing"] },
  { to: "/noticias", icon: "ri-newspaper-line", label: "Noticias", roles: ["super_admin", "admin", "marketing"] },
  { to: "/marketing", icon: "ri-mail-send-line", label: "Marketing", roles: ["super_admin", "admin", "marketing"] },
  { to: "/configuracion", icon: "ri-settings-3-line", label: "Configuración", roles: ["super_admin", "admin"] },
  { to: "/auditoria", icon: "ri-history-line", label: "Auditoría", roles: ["super_admin", "admin"] },
  { to: "/usuarios", icon: "ri-shield-user-line", label: "Usuarios", roles: ["super_admin"] },
  { to: "/perfil", icon: "ri-user-settings-line", label: "Mi Perfil", roles: ["super_admin", "admin", "marketing", "instructor", "evaluador", "estudiante"] },
  { to: "/portal-estudiante", icon: "ri-route-line", label: "Mi Portal", roles: ["estudiante"] },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { user, hasRole, logout } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();

  if (!user) return null;

  const filteredItems = navItems.filter((item) => {
    if (hasRole(...item.roles)) return true;
    // Solo instructores con permiso extra ven el portal evaluador
    if (item.to === "/portal-evaluador" && user.role === "instructor" && user.can_evaluate) return true;
    return false;
  });

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <aside
      className={`fixed top-0 left-0 h-screen bg-[var(--color-bg-secondary)] border-r border-[var(--color-border)] z-40 transition-all duration-300 flex flex-col ${
        collapsed ? "w-[72px]" : "w-[260px]"
      }`}
    >
      <div className="flex items-center gap-3 px-5 h-[64px] border-b border-[var(--color-border)] flex-shrink-0">
        {!collapsed && (
          <div className="flex items-center gap-2.5 overflow-hidden">
            <div className="bg-white/95 rounded-lg px-1.5 py-1 flex items-center justify-center flex-shrink-0">
              <img src={branding.logo_dark || branding.logo_full || LOGO_FULL} alt="KSM" className="h-6 w-auto" />
            </div>
          </div>
        )}
        {collapsed && (
          <div className="bg-white/95 rounded-lg px-1 py-1 flex items-center justify-center flex-shrink-0 mx-auto">
            <img src={branding.logo_icon || LOGO_ICON} alt="KSM" className="h-5 w-auto" />
          </div>
        )}
        <button
          onClick={onToggle}
          className="ml-auto text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors cursor-pointer flex-shrink-0"
        >
          <i className={`ri-${collapsed ? "arrow-right-s" : "arrow-left-s"}-line text-lg`}></i>
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3">
        <div className="space-y-1">
          {filteredItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  isActive
                    ? "bg-[var(--color-accent-muted)] text-[var(--color-text-accent)]"
                    : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-card)] hover:text-[var(--color-text-primary)]"
                }`
              }
            >
              <i className={`${item.icon} text-lg w-5 h-5 flex items-center justify-center flex-shrink-0`}></i>
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </div>
      </nav>

      <div className="p-3 border-t border-[var(--color-border)]">
        <div className={`flex items-center gap-3 ${collapsed ? "justify-center" : "px-2"}`}>
          <div className="w-8 h-8 rounded-full bg-[var(--color-accent)]/20 flex items-center justify-center flex-shrink-0">
            <span className="text-[var(--color-text-accent)] font-semibold text-xs">
              {user.full_name.charAt(0)}
            </span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">{user.full_name}</p>
              <p className="text-[11px] text-[var(--color-text-muted)] truncate">{user.role.replace("_", " ").toUpperCase()}</p>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="text-[var(--color-text-muted)] hover:text-red-400 transition-colors cursor-pointer flex-shrink-0"
            title="Cerrar sesión"
          >
            <i className="ri-logout-box-r-line text-lg w-5 h-5 flex items-center justify-center"></i>
          </button>
        </div>
      </div>
    </aside>
  );
}
